"use client";

import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { LEARNING, LEARNING_PATH } from "../learning.constants";
import type { JobCoursesProps } from "../learning.types";
import {
  LearningError,
  learningRequest,
  recommendCourses,
} from "../services/courseClient";

/** Read bounded, access-scoped recommendations; course details own enquiry/launch actions. */
export function useJobCourses({
  jobId,
  creatorHandle,
  demo = LEARNING.demo,
  enabled = LEARNING.enabled,
}: JobCoursesProps) {
  const access = useQuery({
    queryKey: [LEARNING.queryKey, LEARNING_PATH.access],
    enabled,
    retry: false,
    queryFn: async ({ signal }) => {
      try {
        await learningRequest(LEARNING_PATH.access, undefined, signal);
        return true;
      } catch (error) {
        if (
          error instanceof LearningError &&
          error.status === LEARNING.unauthorized
        )
          return false;
        throw error;
      }
    },
    staleTime: LEARNING.staleTimeMs,
  });
  const courses = useInfiniteQuery({
    queryKey: [LEARNING.queryKey, jobId, creatorHandle, access.data],
    enabled:
      enabled &&
      (access.data === true ||
        ((demo || LEARNING.publicBrowse) && access.data === false)),
    retry: false,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ signal, pageParam }) =>
      recommendCourses(jobId, creatorHandle, signal, pageParam),
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    // A long browsing session should not refetch every loaded page on focus.
    refetchOnWindowFocus: false,
    staleTime: LEARNING.staleTimeMs,
  });

  /** Refresh an expired pagination chain before requesting another page. */
  async function loadMore() {
    if (courses.isFetching) return;
    if (courses.isFetchNextPageError) {
      const refreshed = await courses.refetch();
      if (refreshed.isError || !refreshed.data?.pages.at(-1)?.next_cursor)
        return;
    }
    await courses.fetchNextPage();
  }

  return { access, courses, loadMore };
}
